// 2026-10-17：统一免领取券状态、精确用途和到期时间，详情弹出及临期倒计时共用同一口径。
export interface DetailCoupon {
  customerNoteId: number; status: number; targets: string | number[];
  amount: number; threshold: number; endTime: string | number; startTime?: string | number;
  noteType?: number; discountRate?: number; noteName?: string; orgId?: number; selected?: boolean; customerNoteTarget?: number;
}
export const couponTimestamp = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN
  if (typeof value !== 'string' || !value.trim()) return NaN
  if (/^\d+$/.test(value)) return Number(value)
  return Date.parse(value.includes('T') ? value : value.replace(/-/g, '/'))
}
export function couponTargets(note: Pick<DetailCoupon, 'targets' | 'customerNoteTarget'>): number[] {
  const targets = String(note.targets ?? '').split(',').map(value => Number(value.trim())).filter(value => Number.isInteger(value) && value > 0)
  const specific = Number(note.customerNoteTarget)
  return specific > 0 ? targets.filter(target => target === specific) : targets
}
export function couponAvailable(note: DetailCoupon, now = Date.now()): boolean {
  if (!note) return false
  const start = note.startTime == null ? -Infinity : couponTimestamp(note.startTime)
  return [0, 1].includes(Number(note.status)) && Number.isSafeInteger(Number(note.customerNoteId)) && Number(note.customerNoteId) > 0
    && couponTimestamp(note.endTime) > now && start <= now && Number.isFinite(Number(note.amount)) && Number(note.amount) > 0
}
export function detailCoupons(notes: DetailCoupon[], targets: number[], now: number): DetailCoupon[] {
  const seen = new Set<number>()
  return notes.filter(note => {
    const id = Number(note.customerNoteId)
    if (!couponAvailable(note, now) || seen.has(id) || !couponTargets(note).some(target => targets.includes(target))) return false
    seen.add(id); return true
  }).sort((a, b) => couponTimestamp(a.endTime) - couponTimestamp(b.endTime) || Number(a.customerNoteId) - Number(b.customerNoteId))
}
export function couponSettings(settings: Record<string, any> | any[] = {}) {
  const number = (key: string, fallback: number, max: number) => {
    const raw = Array.isArray(settings) ? settings.find(item => item.key === key)?.value : settings[key]?.value ?? settings[key]
    const value = Number(raw)
    // 2026-10-19：弹出秒数可为0或一位小数；无效旧配置仍回退3秒，临期小时保持整数。
    if (key === 'CouponPopupSeconds') return (typeof raw === 'number' || typeof raw === 'string')
      && /^(?:0|[1-9]\d?)(?:\.\d)?$/.test(String(raw)) && value <= max ? value : fallback
    return Number.isInteger(value) && value >= 1 && value <= max ? value : fallback
  }
  return { popupSeconds: number('CouponPopupSeconds', 3, 60), urgentHours: number('CouponUrgentHours', 24, 720) }
}
export function couponCountdown(endTime: unknown, now: number): string {
  const remaining = Math.max(0, Math.ceil((couponTimestamp(endTime) - now) / 1000))
  if (!Number.isFinite(remaining)) return '00:00:00'
  return [Math.floor(remaining / 3600), Math.floor(remaining / 60) % 60, remaining % 60].map(value => String(value).padStart(2, '0')).join(':')
}

// 2026-10-18：自动使用抵扣最多的单张商品券，同额优先到期早/实例号小，金额按分比较。
const couponCents = (value: unknown) => Math.round((Number(value) + Number.EPSILON) * 100)
export function couponDeduction(note: DetailCoupon, amount: number): number {
  if(Number(note.noteType)!==5)return Number(note.amount)
  const rate=Number(note.discountRate ?? note.amount),cents=couponCents(amount)
  if(!Number.isFinite(rate)||rate<=0||rate>=10||Math.abs(rate*100-Math.round(rate*100))>0.000001||!Number.isFinite(cents)||cents<=0)return 0
  return Math.floor((cents*(1000-Math.round(rate*100)))/1000)/100
}
export function pricedCoupon<T extends DetailCoupon>(note:T, amount:number):T {
  return Number(note.noteType)===5?{...note,discountRate:note.discountRate ?? Number(note.amount),amount:couponDeduction(note,amount)}:note
}
export function couponMeetsAmount(note: DetailCoupon, amount: number): boolean {
  const total = couponCents(amount), threshold = couponCents(note.threshold), discount = couponCents(couponDeduction(note,amount))
  return Number.isFinite(total) && Number.isFinite(threshold) && threshold >= 0 && total > 0
    && discount > 0 && discount <= total && threshold <= total
}
export function bestCoupon<T extends DetailCoupon>(notes: T[], eligible: (note: T) => boolean, now = Date.now(), eligibleAmount?:number): T | undefined {
  return notes.map(note=>eligibleAmount===undefined?note:pricedCoupon(note,eligibleAmount)).filter(note => couponAvailable(note, now) && eligible(note)).sort((a, b) =>
    couponCents(b.amount) - couponCents(a.amount) || couponTimestamp(a.endTime) - couponTimestamp(b.endTime)
    || Number(a.customerNoteId) - Number(b.customerNoteId))[0]
}
// 与服务端整批核销一致：普通混合用途不凑门槛，现货/预售搭配饰品统一使用售卖券。
export function couponForOrder<T extends DetailCoupon>(notes: T[], details: Array<{orderType?: number; dealPrice?: number; orderCount?: number}>, now = Date.now()): T | undefined {
  if (!details.length || details.some(item => !Number.isFinite(Number(item.dealPrice)) || Number(item.dealPrice) < 0
    || !Number.isFinite(Number(item.orderCount)) || Number(item.orderCount) <= 0)) return undefined
  const types = Array.from(new Set(details.map(item => Number(item.orderType))))
  const mapping: Record<number, number> = {1:1, 2:2, 3:3, 10:4, 4:5, 8:6, 9:7, 11:7, 12:8}
  const retail = types.some(type => type === 9 || type === 11) && types.every(type => [4,9,11].includes(type))
  const target = retail ? 7 : types.length === 1 ? mapping[types[0]] : 0
  if (!target) return undefined
  const amount = details.reduce((sum, item) => sum + Number(item.dealPrice) * Number(item.orderCount), 0)
  return bestCoupon(notes, note => couponTargets(note).includes(target) && couponMeetsAmount(note, amount), now,amount)
}
